import axios from 'axios';

/**
 * Authentication service for handling API calls related to user authentication.
 * 
 * This service manages all authentication-related API requests including
 * login, registration, token validation, and user management.
 * 
 * @author BagABoard Team
 */

// Base API URL - will use proxy in development
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const authService = {
  /**
   * Login user with email and password.
   * 
   * @param {string} email - User's email address
   * @param {string} password - User's password
   * @returns {Promise<Object>} - Authentication response with token and user data
   */
  async login(email, password) {
    try {
      const response = await apiClient.post('/auth/login', {
        email,
        password
      });
      return response.data;
    } catch (error) {
      console.error('Login service error:', error);
      throw error;
    }
  },

  /**
   * Register new user account.
   * 
   * @param {Object} userData - User registration data
   * @param {string} userData.firstName - User's first name
   * @param {string} userData.lastName - User's last name
   * @param {string} userData.email - User's email address
   * @param {string} userData.password - User's password
   * @param {string} userData.phoneNumber - User's phone number (optional)
   * @returns {Promise<Object>} - Registration response with token and user data
   */
  async register(userData) {
    try {
      const response = await apiClient.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      console.error('Registration service error:', error);
      throw error;
    }
  },

  /**
   * Validate authentication token.
   * 
   * @param {string} token - JWT token to validate
   * @returns {Promise<boolean>} - Token validity status
   */
  async validateToken(token) {
    try {
      const response = await apiClient.post('/auth/validate', {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data.valid;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  },

  /**
   * Refresh authentication token.
   * 
   * @param {string} token - Current JWT token
   * @returns {Promise<Object>} - New token response
   */
  async refreshToken(token) {
    try {
      const response = await apiClient.post('/auth/refresh', {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  },

  /**
   * Get current user information.
   * 
   * @returns {Promise<Object>} - Current user data
   */
  async getCurrentUser() {
    try {
      // This would typically be a separate endpoint, but we can extract from token validation
      const response = await apiClient.post('/auth/validate');
      return {
        userId: response.data.userId,
        email: response.data.email,
        fullName: response.data.fullName,
        role: response.data.role
      };
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  },

  /**
   * Logout user (client-side cleanup).
   * Server-side logout would be implemented here if needed.
   */
  logout() {
    localStorage.removeItem('token');
  }
};

export default authService;
